import { useCallback, useEffect, useRef, useState } from 'react';

/*
 * Zoom y desplazamiento (pan) para el flipbook.
 *
 * Por qué es una capa aparte y no un cambio de tamaño del libro:
 * react-pageflip congela sus ajustes al construirse y su tamaño se deriva del ancho del
 * contenedor (ver Flipbook.js). Tocar ese ancho para hacer zoom obligaría a recrear la
 * instancia en cada paso. En su lugar el zoom es un `transform` CSS sobre una caja que la
 * librería sigue viendo del mismo tamaño de siempre: nada se recrea.
 *
 * Efecto colateral que hay que manejar: con `scale > 1` la librería mapea el mouse a
 * coordenadas lógicas sin escalar, así que arrastrar para pasar página queda descalibrado.
 * Por eso, con zoom activo, el arrastre desplaza en lugar de pasar página, y los eventos se
 * cortan en fase de captura para que la librería ni se entere (también deja muerto el
 * pasar-página-con-click mientras hay zoom, sin tener que recrear la instancia).
 * Se corta `pointerdown`/`mousedown`/`touchstart`, nunca `click`: así los enlaces del PDF
 * siguen funcionando.
 */

export const ZOOM_MIN = 1;
export const ZOOM_MAX = 3;
export const ZOOM_PASO = 0.25;

const UMBRAL_ARRASTRE = 4; // px que hay que mover antes de considerarlo desplazamiento

const acotar = (valor, min, max) => Math.min(max, Math.max(min, valor));

const distancia = (a, b) => Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);

// Tras un desplazamiento el navegador todavía dispara el `click`: hay que tragárselo para
// no abrir un enlace del PDF sin querer.
const tragarProximoClick = () => {
  const tragar = (e) => { e.stopPropagation(); e.preventDefault(); };
  window.addEventListener('click', tragar, { capture: true, once: true });
  setTimeout(() => window.removeEventListener('click', tragar, { capture: true }), 400);
};

const useZoomPan = ({ habilitado = true } = {}) => {
  // El nodo se monta más tarde que el hook (mientras se renderiza el PDF no existe), así que
  // hace falta una ref de callback con estado: con una ref normal el efecto se engancharía
  // una sola vez, sobre null, y nunca volvería a intentarlo.
  const viewportRef = useRef(null);
  const [viewportEl, setViewportEl] = useState(null);
  const refViewport = useCallback((el) => {
    viewportRef.current = el;
    setViewportEl(el);
  }, []);
  const vistaRef = useRef({ z: 1, tx: 0, ty: 0 });
  const [vista, setVista] = useState({ z: 1, tx: 0, ty: 0, suave: false });
  const [arrastrando, setArrastrando] = useState(false);

  const aplicar = useCallback((siguiente, suave = false) => {
    vistaRef.current = { z: siguiente.z, tx: siguiente.tx, ty: siguiente.ty };
    setVista({ ...vistaRef.current, suave });
  }, []);

  // Mantiene el contenido dentro del hueco: si escalado cabe, se centra; si no, se limita.
  const limitar = useCallback(({ z, tx, ty }) => {
    const el = viewportRef.current;
    const zc = acotar(z, ZOOM_MIN, ZOOM_MAX);
    if (!el) return { z: zc, tx: 0, ty: 0 };
    const anchoV = el.clientWidth;
    const altoV = el.clientHeight;
    const anchoC = anchoV * zc;
    const altoC = altoV * zc;
    return {
      z: zc,
      tx: anchoC <= anchoV ? (anchoV - anchoC) / 2 : acotar(tx, anchoV - anchoC, 0),
      ty: altoC <= altoV ? (altoV - altoC) / 2 : acotar(ty, altoV - altoC, 0),
    };
  }, []);

  /**
   * Lleva el zoom a `destino` manteniendo fijo el punto `ancla` (coordenadas relativas al
   * viewport). Sin ancla, el zoom se hace desde el centro.
   */
  const zoomEn = useCallback((destino, ancla = null, suave = false) => {
    const el = viewportRef.current;
    const { z, tx, ty } = vistaRef.current;
    const zc = acotar(destino, ZOOM_MIN, ZOOM_MAX);
    if (Math.abs(zc - z) < 0.0005) return;
    const ax = ancla ? ancla.x : (el ? el.clientWidth / 2 : 0);
    const ay = ancla ? ancla.y : (el ? el.clientHeight / 2 : 0);
    // El punto del contenido bajo el ancla debe seguir bajo el ancla tras escalar
    const cx = (ax - tx) / z;
    const cy = (ay - ty) / z;
    aplicar(limitar({ z: zc, tx: ax - cx * zc, ty: ay - cy * zc }), suave);
  }, [aplicar, limitar]);

  const reiniciar = useCallback((suave = true) => {
    if (vistaRef.current.z === 1 && vistaRef.current.tx === 0 && vistaRef.current.ty === 0) return;
    aplicar({ z: 1, tx: 0, ty: 0 }, suave);
  }, [aplicar]);

  // Cuando cambia el hueco (resize, pantalla completa, giro) se re-limita en vez de resetear
  const reajustar = useCallback(() => {
    if (vistaRef.current.z === 1) return;
    aplicar(limitar(vistaRef.current), false);
  }, [aplicar, limitar]);

  useEffect(() => {
    const el = viewportEl;
    if (!el || !habilitado) return undefined;

    const puntoLocal = (clientX, clientY) => {
      const r = el.getBoundingClientRect();
      return { x: clientX - r.left, y: clientY - r.top };
    };

    // ---------- rueda del mouse ----------
    const alRodar = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const delta = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
      zoomEn(vistaRef.current.z * Math.exp(-delta * 0.0015), puntoLocal(e.clientX, e.clientY));
    };

    // ---------- desplazamiento con mouse ----------
    let panMouse = null;

    const alBajarMouse = (e) => {
      if (vistaRef.current.z <= 1 || e.button !== 0) return; // sin zoom manda la librería
      e.stopPropagation();
      // Sin esto el navegador inicia su arrastre nativo de imagen y deja de entregar
      // mousemove/mouseup: el desplazamiento se quedaría pegado al soltar el botón
      e.preventDefault();
      panMouse = {
        x: e.clientX,
        y: e.clientY,
        tx: vistaRef.current.tx,
        ty: vistaRef.current.ty,
        movido: false,
      };
      setArrastrando(true);
    };

    const alMoverMouse = (e) => {
      if (!panMouse) return;
      // Red de seguridad: si llega un movimiento sin botones pulsados, el mouseup se perdió
      // (soltaste fuera de la ventana, otra pestaña, un menú contextual)
      if (e.buttons === 0) { alSoltarMouse(); return; }
      const dx = e.clientX - panMouse.x;
      const dy = e.clientY - panMouse.y;
      if (!panMouse.movido && Math.hypot(dx, dy) > UMBRAL_ARRASTRE) panMouse.movido = true;
      aplicar(limitar({ z: vistaRef.current.z, tx: panMouse.tx + dx, ty: panMouse.ty + dy }));
    };

    // Cada arrastre parte del desplazamiento actual, así que soltar y volver a agarrar
    // permite recorrer el documento entero por tramos
    function alSoltarMouse() {
      if (!panMouse) return;
      if (panMouse.movido) tragarProximoClick();
      panMouse = null;
      setArrastrando(false);
    }

    const alPerderFoco = () => alSoltarMouse();

    // ---------- pellizco y desplazamiento táctil ----------
    let pellizco = null;
    let panTactil = null;

    const iniciarPanTactil = (t) => {
      panTactil = {
        x: t.clientX,
        y: t.clientY,
        tx: vistaRef.current.tx,
        ty: vistaRef.current.ty,
        movido: false,
      };
    };

    const alTocar = (e) => {
      if (e.touches.length === 2) {
        e.stopPropagation(); // que la librería no lo lea como swipe
        panTactil = null;
        pellizco = { d0: distancia(e.touches[0], e.touches[1]), z0: vistaRef.current.z };
        setArrastrando(true);
        return;
      }
      if (e.touches.length === 1 && vistaRef.current.z > 1) {
        e.stopPropagation();
        iniciarPanTactil(e.touches[0]);
        setArrastrando(true);
      }
    };

    const alMoverDedo = (e) => {
      if (pellizco && e.touches.length >= 2) {
        e.preventDefault();
        e.stopPropagation();
        const [a, b] = [e.touches[0], e.touches[1]];
        const d = distancia(a, b);
        if (pellizco.d0 > 0) {
          const medio = puntoLocal((a.clientX + b.clientX) / 2, (a.clientY + b.clientY) / 2);
          zoomEn(pellizco.z0 * (d / pellizco.d0), medio);
        }
        return;
      }
      if (panTactil && e.touches.length === 1) {
        e.preventDefault();
        e.stopPropagation();
        const t = e.touches[0];
        const dx = t.clientX - panTactil.x;
        const dy = t.clientY - panTactil.y;
        if (!panTactil.movido && Math.hypot(dx, dy) > UMBRAL_ARRASTRE) panTactil.movido = true;
        aplicar(limitar({ z: vistaRef.current.z, tx: panTactil.tx + dx, ty: panTactil.ty + dy }));
      }
    };

    const alLevantarDedo = (e) => {
      if (pellizco && e.touches.length < 2) {
        e.stopPropagation();
        pellizco = null;
        // Si queda un dedo y sigue habiendo zoom, el gesto continúa como desplazamiento
        if (e.touches.length === 1 && vistaRef.current.z > 1) iniciarPanTactil(e.touches[0]);
      }
      if (panTactil && e.touches.length === 0) {
        e.stopPropagation();
        if (panTactil.movido) tragarProximoClick();
        panTactil = null;
      }
      if (e.touches.length === 0) setArrastrando(false);
    };

    const captura = { capture: true, passive: false };
    el.addEventListener('wheel', alRodar, captura);
    el.addEventListener('mousedown', alBajarMouse, captura);
    el.addEventListener('touchstart', alTocar, captura);
    el.addEventListener('touchmove', alMoverDedo, captura);
    el.addEventListener('touchend', alLevantarDedo, captura);
    el.addEventListener('touchcancel', alLevantarDedo, captura);
    window.addEventListener('mousemove', alMoverMouse);
    window.addEventListener('mouseup', alSoltarMouse);
    window.addEventListener('blur', alPerderFoco);
    window.addEventListener('contextmenu', alPerderFoco);

    return () => {
      el.removeEventListener('wheel', alRodar, captura);
      el.removeEventListener('mousedown', alBajarMouse, captura);
      el.removeEventListener('touchstart', alTocar, captura);
      el.removeEventListener('touchmove', alMoverDedo, captura);
      el.removeEventListener('touchend', alLevantarDedo, captura);
      el.removeEventListener('touchcancel', alLevantarDedo, captura);
      window.removeEventListener('mousemove', alMoverMouse);
      window.removeEventListener('mouseup', alSoltarMouse);
      window.removeEventListener('blur', alPerderFoco);
      window.removeEventListener('contextmenu', alPerderFoco);
    };
  }, [viewportEl, habilitado, zoomEn, aplicar, limitar]);

  return {
    refViewport,
    zoom: vista.z,
    conZoom: vista.z > 1,
    arrastrando,
    // Sólo se emite transform con zoom: sin él la caja queda exactamente como estaba
    estiloTransform: vista.z === 1
      ? undefined
      : {
        // `translate` 2D a propósito: `translate3d` promueve a capa de composición y el
        // navegador escalaría una textura ya rasterizada en vez de redibujar nítido
        transform: `translate(${vista.tx}px, ${vista.ty}px) scale(${vista.z})`,
        transition: vista.suave ? 'transform 180ms ease-out' : 'none',
      },
    zoomEn,
    reiniciar,
    reajustar,
  };
};

export default useZoomPan;
